import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_INSIGHTS_SUMMARY } from './operations';
import { insightsStrings } from '../../content/insights';
import { getHobbyMediaStyle, HobbyMediaIcon } from '../../content/hobbyMedia';
import InsightsRecapShareModal from '../../components/shareHighlights/InsightsRecapShareModal';
import AccessibleModal from '../../components/common/AccessibleModal';
import RatingReadout from '../../components/common/RatingReadout';
import { trackTelemetry } from '../../services/telemetry';

const WINDOWS = [
  { value: '7d', label: insightsStrings.windows.labels.sevenDays },
  { value: '30d', label: insightsStrings.windows.labels.thirtyDays },
  { value: '90d', label: insightsStrings.windows.labels.ninetyDays },
  { value: 'year', label: insightsStrings.windows.labels.year },
  { value: 'all', label: insightsStrings.windows.labels.all },
];

/**
 * Retrieves window label.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function getWindowLabel(value) {
  return WINDOWS.find((option) => option.value === value)?.label || insightsStrings.windows.labels.fallback;
}

/**
 * Normalizes delta.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function formatDelta(value) {
  const delta = Number(value || 0);
  if (delta > 0) return `+${delta}`;
  return String(delta);
}

/**
 * Renders the insights loading state experience.
 *
 * @returns {JSX.Element} The rendered interface.
 */
function InsightsLoadingState() {
  return (
    <div className="insights-page">
      <RecapHeroSkeleton windowValue="30d" onWindowChange={() => {}} />
      <RecapStatsSkeleton />
    </div>
  );
}

/**
 * Renders the insights error state experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.onRetry - Callback invoked when retry occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function InsightsErrorState({ onRetry }) {
  return (
    <div className="insights-page">
      <section className="feed-hero insights-hero">
        <div>
          <p className="eyebrow">{insightsStrings.title}</p>
          <h2>{insightsStrings.error.title}</h2>
          <p>{insightsStrings.error.detail}</p>
          <div className="today-actions">
            <button type="button" className="btn-primary" onClick={onRetry}>{insightsStrings.error.retry}</button>
            <Link className="btn-secondary" to="/">{insightsStrings.error.openHome}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Renders the window selector experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function WindowSelector({ value, onChange }) {
  return (
    <div className="insights-window-selector" aria-label={insightsStrings.windows.selectorLabel}>
      {WINDOWS.map((windowOption) => (
        <button
          type="button"
          key={windowOption.value}
          className={value === windowOption.value ? 'active' : ''}
          onClick={() => onChange(windowOption.value)}
        >
          {windowOption.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Renders the metric card experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.label - label value.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.note - note value.
 * @returns {JSX.Element} The rendered interface.
 */
function MetricCard({ label, value, note, ratingValue }) {
  return (
    <article className="insight-card">
      <span>{label}</span>
      <strong>{ratingValue > 0 ? <RatingReadout rating={ratingValue} /> : value}</strong>
      {note && <p>{note}</p>}
    </article>
  );
}

/**
 * Renders the recap stat card experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.label - label value.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.note - note value.
 * @param {*} props.tone - tone value.
 * @returns {JSX.Element} The rendered interface.
 */
function RecapStatCard({ label, value, note, tone }) {
  return (
    <article className={`insight-card recap-stat-card ${tone || 'neutral'}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      {note && <p>{note}</p>}
    </article>
  );
}

/**
 * Renders the recap hero skeleton experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.windowValue - window value value.
 * @param {*} props.onWindowChange - Callback invoked when window change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function RecapHeroSkeleton({ windowValue, onWindowChange }) {
  return (
    <section className="feed-hero insights-hero insights-recap-hero insights-recap-skeleton" aria-busy="true">
      <div className="insights-recap-skeleton-copy">
        <span className="skeleton-line compact" />
        <span className="skeleton-line title" />
        <span className="skeleton-line long" />
        <span className="skeleton-line medium" />
      </div>
      <WindowSelector value={windowValue} onChange={onWindowChange} />
    </section>
  );
}

/**
 * Renders the recap stats skeleton experience.
 *
 * @returns {JSX.Element} The rendered interface.
 */
function RecapStatsSkeleton() {
  return (
    <section className="insight-grid insight-recap-grid" aria-busy="true">
      {['primary', 'warm', 'cool', 'soft'].map((tone) => (
        <article className={`insight-card recap-stat-card recap-stat-skeleton ${tone}`} key={tone}>
          <span className="skeleton-line compact" />
          <span className="skeleton-line value" />
          <span className="skeleton-line long" />
          <span className="skeleton-line short" />
        </article>
      ))}
    </section>
  );
}

/**
 * Renders the activity pulse chart experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.points - points value.
 * @returns {JSX.Element} The rendered interface.
 */
function ActivityPulseChart({ points = [] }) {
  const max = Math.max(...points.map((point) => Number(point.value || 0)), 1);
  const rangeLabel = points.length > 0 ? `${shortDate(points[0].label)} - ${shortDate(points[points.length - 1].label)}` : insightsStrings.dates.recently;
  return (
    <div className="insight-pulse-wrap">
      <div className="insight-pulse-legend">
        <strong>{rangeLabel}</strong>
        <span>
          {insightsStrings.charts.pulseLess}
          <i />
          <i className="mid" />
          <i className="high" />
          {insightsStrings.charts.pulseMore}
        </span>
      </div>
      <div className="insight-pulse-days" aria-hidden="true">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => <span key={`${day}-${index}`}>{day}</span>)}
      </div>
      <div className="insight-pulse-grid" aria-label="Activity by day">
        {points.map((point) => {
          const value = Number(point.value || 0);
          const intensity = value === 0 ? 0 : Math.max(0.22, value / max);
          return (
            <span
              key={point.label}
              title={`${point.label}: ${value}`}
              style={{ '--pulse-opacity': intensity }}
              className={value > 0 ? 'active' : ''}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Renders the rating distribution chart experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.points - points value.
 * @returns {JSX.Element} The rendered interface.
 */
function RatingDistributionChart({ points = [] }) {
  const max = Math.max(...points.map((point) => Number(point.value || 0)), 1);
  return (
    <div className="insight-rating-chart">
      {points.map((point) => {
        const value = Number(point.value || 0);
        return (
          <div className="insight-rating-column" key={point.label}>
            <div>
              <i style={{ height: `${Math.max((value / max) * 100, value > 0 ? 10 : 0)}%` }} />
            </div>
            <strong aria-label={`${point.label} stars`}>{point.label}★</strong>
            <span>{value}</span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Performs the short date operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function shortDate(value) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Renders the hobby insight mark experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.hobbyId - hobby id value.
 * @param {*} props.className - class name value.
 * @returns {JSX.Element} The rendered interface.
 */
function HobbyInsightMark({ hobbyId, className = '' }) {
  const style = getHobbyMediaStyle(hobbyId);
  return (
    <span
      className={`hobby-highlight-icon insight-hobby-mark ${className}`.trim()}
      style={{ '--hobby-accent': style.accent }}
      aria-hidden="true"
    >
      <HobbyMediaIcon type={hobbyId} />
    </span>
  );
}

/** Shows a standout's entry artwork, falling back to its hobby mark. */
function StandoutMedia({ standout, hobbyId }) {
  const [failed, setFailed] = useState(false);
  if (!standout.imageUrl || failed) return <HobbyInsightMark hobbyId={hobbyId} />;
  return (
    <img
      className="insight-standout-image"
      src={standout.imageUrl}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

/**
 * Renders the insight chart section experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.section - section value.
 * @param {*} props.compact - compact value.
 * @returns {JSX.Element} The rendered interface.
 */
function InsightChartSection({ section, compact = false }) {
  const points = section?.points || [];
  const max = Math.max(...points.map((item) => Number(item.value || 0)), 1);
  const total = Math.max(points.reduce((sum, item) => sum + Number(item.value || 0), 0), 1);
  return (
    <section className={`feed-section insight-chart-panel insight-premium-chart${compact ? ' compact' : ''}`}>
      <div className="section-heading">
        <div>
          <p className="eyebrow">{insightsStrings.charts.patternEyebrow}</p>
          <h3>{section.title}</h3>
          {section.subtitle && <span>{section.subtitle}</span>}
        </div>
      </div>
      {points.length === 0 ? (
        <div className="home-empty-panel compact">
          <p>{insightsStrings.charts.empty}</p>
        </div>
      ) : section.kind === 'heatmap' ? (
        <ActivityPulseChart points={points} />
      ) : section.id === 'mix' ? (
        <div className="insight-mix-stack">
          <div className="insight-mix-bar" aria-label="Hobby mix">
            {points.map((point) => {
              const style = getHobbyMediaStyle(point.label);
              return (
                <i
                  key={point.label}
                  title={`${point.label}: ${point.value}`}
                  style={{
                    '--hobby-accent': style.accent,
                    width: `${Math.max((Number(point.value || 0) / total) * 100, 4)}%`,
                  }}
                />
              );
            })}
          </div>
          <div className="insight-mix-legend">
            {points.map((point) => {
              const style = getHobbyMediaStyle(point.label);
              return (
                <span key={point.label} style={{ '--hobby-accent': style.accent }}>
                  <i />
                  <strong>{point.label}</strong>
                  <small>{Math.round((Number(point.value || 0) / total) * 100)}%</small>
                </span>
              );
            })}
          </div>
        </div>
      ) : section.kind === 'histogram' ? (
        <RatingDistributionChart points={points} />
      ) : (
        <div className="insight-bar-chart">
          {points.map((point) => {
            return (
              <div className="insight-bar-row" key={point.label}>
                <span>{point.label}</span>
                <div><i style={{ width: `${Math.max((Number(point.value || 0) / max) * 100, 4)}%` }} /></div>
                <strong>{point.value}</strong>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Renders the momentum by hobby chart experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.hobbies - hobbies collection.
 * @returns {JSX.Element} The rendered interface.
 */
function MomentumByHobbyChart({ hobbies = [] }) {
  const points = hobbies
    .map((hobby) => ({
      label: hobby.hobby,
      value: Number(hobby.delta || 0),
      magnitude: Math.abs(Number(hobby.delta || 0)),
    }))
    .sort((a, b) => {
      if (b.magnitude !== a.magnitude) return b.magnitude - a.magnitude;
      return a.label.localeCompare(b.label);
    });
  const max = Math.max(...points.map((point) => point.magnitude), 1);

  return (
    <section className="feed-section insight-chart-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{insightsStrings.momentum.eyebrow}</p>
          <h3>{insightsStrings.momentum.byHobbyTitle}</h3>
        </div>
      </div>
      {points.length === 0 ? (
        <div className="home-empty-panel compact">
          <p>{insightsStrings.momentum.empty}</p>
        </div>
      ) : (
        <div className="insight-bar-chart">
          {points.map((point) => {
            const tone = point.value > 0 ? 'positive' : point.value < 0 ? 'negative' : 'neutral';
            return (
              <div className={`insight-bar-row ${tone}`} key={point.label}>
                <span>{point.label}</span>
                <div><i style={{ width: `${Math.max((point.magnitude / max) * 100, 4)}%` }} /></div>
                <strong>{formatDelta(point.value)}</strong>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * Renders the deep dive card experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.section - section value.
 * @param {*} props.onSelect - Callback invoked when select occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function DeepDiveCard({ section, onSelect }) {
  const visibleCards = section.atAGlance?.slice(0, 2) || [];
  return (
    <button className="insight-deep-dive-card" type="button" onClick={onSelect}>
      <div className="insight-deep-dive-heading">
        <HobbyInsightMark hobbyId={section.hobbyId} />
        <div>
          <h3>{section.hobby}</h3>
          <p>{section.summary}</p>
        </div>
        <span className="insight-chevron" aria-hidden="true">›</span>
      </div>
      <div className="insight-deep-dive-metrics">
        {visibleCards.map((cardItem) => (
          <span key={`${section.hobbyId}-${cardItem.label}`}>
            <strong>{cardItem.ratingValue > 0 ? <RatingReadout rating={cardItem.ratingValue} /> : cardItem.value}</strong>
            <small>{cardItem.label}</small>
          </span>
        ))}
      </div>
    </button>
  );
}

/**
 * Renders the fitness progress story experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.progress - progress value.
 * @returns {JSX.Element} The rendered interface.
 */
function FitnessProgressStory({ progress }) {
  if (!progress) return null;
  const scheduled = Number(progress.scheduledThisWeek || 0);
  const completed = Number(progress.completedThisWeek || 0);
  const percent = scheduled ? Math.min((completed / scheduled) * 100, 100) : 0;
  const routineHref = progress.nextPlanId
    ? `/hobby/fitness/routine/${encodeURIComponent(progress.nextPlanId)}`
    : '/hobby/fitness/routine/new';
  return (
    <div className="fitness-insight-story">
      <section className="fitness-insight-week">
        <div>
          <span>{insightsStrings.fitness.weeklyProgress}</span>
          <strong>{scheduled ? insightsStrings.fitness.routineProgress(completed, scheduled) : insightsStrings.fitness.noRoutine}</strong>
          {scheduled > 0 && <small>{insightsStrings.fitness.remaining(progress.remainingThisWeek)}</small>}
        </div>
        {scheduled > 0 && <b>{progress.adherenceRate}%</b>}
        <div className="fitness-insight-progress"><i style={{ width: `${percent}%` }} /></div>
      </section>
      {progress.nextDayTitle && (
        <Link className="fitness-insight-next" to={routineHref}>
          <span>{insightsStrings.fitness.nextWorkout}</span>
          <strong>{progress.nextDayTitle}</strong>
          <small>{[progress.nextPlanTitle, progress.nextWeekday].filter(Boolean).join(' • ')}</small>
          <b aria-hidden="true">›</b>
        </Link>
      )}
      <div className="fitness-insight-charts">
        <FitnessMiniTrend title={insightsStrings.fitness.adherenceTrend} detail={insightsStrings.fitness.adherenceTrendDetail} points={progress.weeklyAdherence} suffix="%" />
        <FitnessMiniTrend title={insightsStrings.fitness.minutesTrend} detail={insightsStrings.fitness.minutesTrendDetail} points={progress.weeklyMinutes} suffix=" min" />
      </div>
    </div>
  );
}

/**
 * Renders the fitness mini trend experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.title - title value.
 * @param {*} props.detail - detail value.
 * @param {*} props.points - points value.
 * @param {*} props.suffix - suffix value.
 * @returns {JSX.Element} The rendered interface.
 */
function FitnessMiniTrend({ title, detail, points = [], suffix }) {
  const max = Math.max(...points.map((point) => Number(point.value || 0)), 1);
  return (
    <section className="fitness-mini-trend">
      <strong>{title}</strong><small>{detail}</small>
      <div>{points.map((point) => <span key={point.label} title={`${shortDate(point.label)}: ${point.value}${suffix}`}><i style={{ height: `${Math.max((Number(point.value || 0) / max) * 100, point.value ? 8 : 2)}%` }} /></span>)}</div>
    </section>
  );
}

/**
 * Renders the deep dive modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.section - section value.
 * @param {*} props.fitnessProgress - fitness progress value.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function DeepDiveModal({ section, fitnessProgress, onClose }) {
  if (!section) return null;
  return (
    <AccessibleModal
      className="insight-deep-dive-modal"
      overlayClassName="insight-deep-dive-overlay"
      labelledBy="insight-deep-dive-title"
      onClose={onClose}
    >
        <div className="modal-header">
          <div className="insight-modal-title">
            <HobbyInsightMark hobbyId={section.hobbyId} />
            <div>
              <p className="eyebrow">{insightsStrings.deepDives.detailEyebrow}</p>
              <h3 id="insight-deep-dive-title" tabIndex="-1" data-modal-initial-focus>{section.hobby}</h3>
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">×</button>
        </div>
        <p className="insight-deep-dive-summary">{section.summary}</p>
        <section className="insight-deep-dive-section">
          <h4>{insightsStrings.deepDives.atAGlance}</h4>
          <div className="insight-scoreboard-grid">
            {(section.atAGlance || []).map((cardItem) => (
              <MetricCard key={`${section.hobbyId}-${cardItem.label}`} label={cardItem.label} value={cardItem.value} note={cardItem.detail} ratingValue={cardItem.ratingValue} />
            ))}
          </div>
        </section>
        {section.consistency && (
          <section className="insight-deep-dive-section">
            <h4>{insightsStrings.deepDives.consistency}</h4>
            <div className="insight-scoreboard-grid single">
              <MetricCard label={section.consistency.label} value={section.consistency.value} note={section.consistency.detail} ratingValue={section.consistency.ratingValue} />
            </div>
          </section>
        )}
        {section.hobbyId === 'fitness' && <FitnessProgressStory progress={fitnessProgress} />}
        {(section.charts || []).map((chart) => <InsightChartSection key={chart.id} section={chart} compact />)}
        {(section.patterns || []).length > 0 && (
          <section className="insight-deep-dive-section">
            <h4>{insightsStrings.deepDives.patterns}</h4>
            <div className="insight-pattern-list">
              {section.patterns.map((pattern) => (
                <article className={`insight-pattern-card ${pattern.tone || 'neutral'}`} key={pattern.title}>
                  <strong>{pattern.title}</strong>
                  <p>{pattern.body}</p>
                </article>
              ))}
            </div>
          </section>
        )}
        {(section.standouts || []).length > 0 && (
          <section className="insight-deep-dive-section">
            <h4>{insightsStrings.deepDives.standouts}</h4>
            <div className="insight-standout-list">
              {section.standouts.map((standout) => (
                <article className="insight-standout-row" key={`${standout.targetId || standout.title}-${standout.subtitle || ''}`}>
                  <StandoutMedia key={standout.imageUrl || 'fallback'} standout={standout} hobbyId={section.hobbyId} />
                  <div>
                    {standout.subtitle && <span>{standout.subtitle}</span>}
                    <strong>{standout.title}</strong>
                    {standout.ratingValue > 0 ? <RatingReadout rating={standout.ratingValue} /> : standout.value && <small>{standout.value}</small>}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
        <Link className="btn-primary insight-modal-action" to={`/hobby/${section.hobbyId}`} onClick={onClose}>
          {section.nextAction?.label || insightsStrings.deepDives.open(section.hobby)}
        </Link>
    </AccessibleModal>
  );
}

/**
 * Renders the notable patterns experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.patterns - patterns value.
 * @returns {JSX.Element} The rendered interface.
 */
function NotablePatterns({ patterns = [] }) {
  return (
    <section className="feed-section insight-pattern-section">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{insightsStrings.patterns.eyebrow}</p>
          <h3>{insightsStrings.patterns.title}</h3>
        </div>
      </div>
      {patterns.length === 0 ? (
        <div className="home-empty-panel compact">
          <p>{insightsStrings.patterns.empty}</p>
        </div>
      ) : (
        <div className="insight-pattern-list">
          {patterns.map((pattern) => (
            <article className={`insight-pattern-card ${pattern.tone || 'neutral'}`} key={pattern.title}>
              <strong>{pattern.title}</strong>
              <p>{pattern.body}</p>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

/** Loads a selected Insights window and presents recap, charts, patterns, and deep dives. */
export default function InsightsView() {
  const [windowValue, setWindowValue] = useState('30d');
  const [selectedDeepDive, setSelectedDeepDive] = useState(null);
  const [showRecapShare, setShowRecapShare] = useState(false);
  const { loading, error, data, previousData, refetch } = useQuery(GET_INSIGHTS_SUMMARY, {
    variables: { window: windowValue },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });
  const summary = data?.getInsightsSummary || previousData?.getInsightsSummary;
  const chartSections = summary?.chartSections || [];
  const deepDives = summary?.hobbyDeepDives || [];
  const patternCards = [...(summary?.crossHobbyPatterns || []), ...(summary?.notablePatterns || [])].slice(0, 6);
  const topHobbies = useMemo(() => [...(summary?.hobbyInsights || [])].sort((a, b) => b.entries - a.entries), [summary]);

  if (loading && !summary) return <InsightsLoadingState />;
  if (error && !summary) return <InsightsErrorState onRetry={() => refetch()} />;

  const recap = summary?.weeklyRecap || {};
  const windowLabel = getWindowLabel(windowValue);
  const hasInsightActivity = Number(recap.totalUpdates || 0) > 0 || topHobbies.length > 0;
  const overviewCards = [
    { label: insightsStrings.recap.overview.updates, value: String(recap.totalUpdates ?? 0), note: insightsStrings.recap.overview.savedIn(windowLabel), tone: 'primary' },
    { label: insightsStrings.recap.overview.activeHobbies, value: String(recap.activeHobbies ?? 0), note: recap.mostActiveHobby ? insightsStrings.recap.overview.ledTheWay(recap.mostActiveHobby) : insightsStrings.recap.overview.startSmall, tone: 'warm' },
    { label: insightsStrings.recap.overview.activeDays, value: String(recap.activeDays ?? 0), note: insightsStrings.recap.overview.activeDaysNote, tone: 'cool' },
    { label: insightsStrings.recap.overview.favorites, value: String(recap.favoritesAdded ?? 0), note: insightsStrings.recap.overview.favoritesNote, tone: 'soft' },
  ];

  return (
    <div className="insights-page insights-v2-page">
      {loading && !summary ? (
        <RecapHeroSkeleton windowValue={windowValue} onWindowChange={setWindowValue} />
      ) : (
        <section className="feed-hero insights-hero insights-recap-hero">
          <div>
            <p className="eyebrow">{insightsStrings.recap.eyebrow(windowLabel)}</p>
            <h2>{recap.title || insightsStrings.recap.defaultTitle}</h2>
            <p>{recap.summary || insightsStrings.empty.detail}</p>
          </div>
          <div className="insights-hero-actions">
            {hasInsightActivity && <button type="button" className="insights-share-action" onClick={() => { setShowRecapShare(true); trackTelemetry('insights.recap.opened', { window: windowValue }); }}><span aria-hidden="true">↗</span>{insightsStrings.shareRecap.action}</button>}
            <WindowSelector value={windowValue} onChange={setWindowValue} />
          </div>
        </section>
      )}

      {loading && !summary ? (
        <RecapStatsSkeleton />
      ) : (
        <section className="insight-grid insight-recap-grid">
          {overviewCards.map((card) => <RecapStatCard key={card.label} {...card} />)}
        </section>
      )}

      {hasInsightActivity && (
        <section className="feed-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">{insightsStrings.charts.patternsEyebrow}</p>
              <h3>{insightsStrings.charts.patternsTitle}</h3>
            </div>
          </div>
          <div className="insights-layout insight-chart-layout">
            {chartSections.length > 0 ? (
              chartSections.map((section) => <InsightChartSection key={section.id} section={section} />)
            ) : (
              <MomentumByHobbyChart hobbies={topHobbies} />
            )}
          </div>
        </section>
      )}

      <section className="feed-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">{insightsStrings.deepDives.eyebrow}</p>
            <h3>{insightsStrings.deepDives.title}</h3>
          </div>
        </div>
        {deepDives.length === 0 ? (
          <div className="home-empty-panel insights-empty-panel">
            <h4>{insightsStrings.empty.summaryTitle}</h4>
            <p>{insightsStrings.empty.summaryDetail}</p>
          </div>
        ) : (
          <div className="insight-deep-dive-grid">
            {deepDives.map((section) => (
              <DeepDiveCard
                key={section.hobbyId}
                section={section}
                onSelect={() => setSelectedDeepDive(section)}
              />
            ))}
          </div>
        )}
      </section>

      <NotablePatterns patterns={patternCards} />
      <DeepDiveModal section={selectedDeepDive} fitnessProgress={summary?.fitnessProgress} onClose={() => setSelectedDeepDive(null)} />
      {showRecapShare && summary && <InsightsRecapShareModal initialSummary={summary} initialWindow={windowValue} onClose={() => setShowRecapShare(false)} />}
    </div>
  );
}
